import type { PendingSourceFragment } from '@mnd-apps/publish/generated/types';
import { useMediaQuery } from '@mnd-frontend/hooks';
import IconPen from '@mnd-frontend/icons/components/Pen';
import IconThumbnail from '@mnd-frontend/icons/components/Thumbnail';
import { Button, Text } from '@mnd-frontend/ui/publish/components';
import { media } from '@mnd-frontend/ui/publish/components/utils';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { GREY_200, RADIUS_LG, RADIUS_MD, SIZING_6, SIZING_8 } from '@mnd-frontend/ui/shared/tokens';
import {
  type Dispatch,
  lazy,
  type MutableRefObject,
  type SetStateAction,
  Suspense,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PendingSourceDocument, usePendingSourceUpdateMutation } from '../../../generated/hooks';
import { STEP } from '../types';
import { useOnboardingLocaleText } from '../useOnboardingLocaleText';

const OnboardingModalBrand = lazy(() => import('../OnboardingModal'));

const LogoFrame = styled.div`
  width: ${SIZING_8};
  height: ${SIZING_8};
  background-color: white;
  border-radius: ${RADIUS_MD};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BrandLogoContainer = styled.div`
  width: 5.5rem;
  min-height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;

  .logo-img {
    object-fit: contain;
    height: auto;
    width: 100%;
    max-width: ${SIZING_6};
  }
`;

const BrandSearchNameWrapper = styled.div`
  flex: 1;
  justify-items: self-start;
`;

const TextWrapBrandUrl = styled.div`
  > p {
    overflow-wrap: anywhere;

    ${media.greaterThan('phablet')} {
      overflow-wrap: normal;
    }
  }
`;

const BrandSearchProfileContainer = styled.div<{ $isMainBrand?: boolean }>`
  display: flex;
  flex-direction: row;
  padding: 1rem ${({ $isMainBrand = false }) => ($isMainBrand ? '2rem' : '1rem')};
  align-items: center;
  gap: 1rem;
  border-radius: ${RADIUS_LG};
  background-color: ${GREY_200};
  height: ${({ $isMainBrand = false }) => ($isMainBrand ? '8.25rem' : 'auto')};

  ${media.lessThan('tablet')} {
    padding: 1rem;
  }
`;

type StepBrandProps = {
  originalPendingSource: PendingSourceFragment;
  setSelectedBrandIds: Dispatch<SetStateAction<string[]>>;
  trackBrandNameChange: MutableRefObject<boolean>;
  trackBrandUrlChange: MutableRefObject<boolean>;
};

const StepBrand = ({
  originalPendingSource,
  setSelectedBrandIds,
  trackBrandNameChange,
  trackBrandUrlChange,
}: StepBrandProps) => {
  const { t } = useTranslation();
  const { showFailureToast } = useToasts();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const localeText = useOnboardingLocaleText()[STEP.BRAND];
  const isPhablet = useMediaQuery(media.greaterThan('phablet'));

  const [brandName, setBrandName] = useState(originalPendingSource.brandName ?? '');
  const [brandUrl, setBrandUrl] = useState(originalPendingSource.brandUrl ?? '');
  const initialRef = useRef({
    currentBrandName: originalPendingSource.brandName,
    currentBrandUrl: originalPendingSource.brandUrl,
  });

  // User edits her own brand
  const [updatePendingSource, { loading, data, reset }] = usePendingSourceUpdateMutation({
    refetchQueries: [PendingSourceDocument, 'PendingSource'],
    onCompleted: newData => {
      const error = newData.pendingSourceUpdate?.error;
      if (error) {
        if (error.__typename !== 'PendingSourceUpdateValidationError') {
          showFailureToast(error.message);
        }
      } else {
        trackBrandNameChange.current = brandName !== initialRef.current.currentBrandName;
        trackBrandUrlChange.current = brandUrl !== initialRef.current.currentBrandUrl;
        setSelectedBrandIds([]);
        setIsEditModalOpen(false);
      }
    },
    onError: () => showFailureToast(t('onboarding_trial_error_update')),
  });

  return (
    <>
      <BrandSearchProfileContainer $isMainBrand>
        <LogoFrame>
          {originalPendingSource?.logoUrl ? (
            <BrandLogoContainer>
              <img
                className="logo-img"
                src={originalPendingSource.logoUrl}
                alt=""
                width="48"
                height="48"
              />
            </BrandLogoContainer>
          ) : (
            <IconThumbnail $size="md" />
          )}
        </LogoFrame>
        <BrandSearchNameWrapper>
          <Text as="p" variant="headlineSmall" mobileVariant="labelMedium">
            {originalPendingSource.brandName}
          </Text>
          <TextWrapBrandUrl>
            <Text as="p" variant="labelMedium" mobileVariant="labelSmall">
              {originalPendingSource.brandUrl}
            </Text>
          </TextWrapBrandUrl>
        </BrandSearchNameWrapper>
        <Button
          type="button"
          configuration="text"
          leadingIcon={<IconPen aria-hidden />}
          onClick={() => {
            setIsEditModalOpen(true);
          }}
          {...(isPhablet ? {} : { 'aria-label': t('common_edit') })}
        >
          {isPhablet ? t('common_edit') : ''}
        </Button>
      </BrandSearchProfileContainer>
      <Suspense>
        <OnboardingModalBrand
          type={STEP.BRAND}
          currentStepLocaleText={localeText}
          pendingSourceId={originalPendingSource.id}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onUpdateMutation={updatePendingSource}
          onUpdateReset={reset}
          updating={loading}
          updatedSource={data?.pendingSourceUpdate}
          fieldController={{ brandName, setBrandName, brandUrl, setBrandUrl }}
        />
      </Suspense>
    </>
  );
};

export default StepBrand;
